import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";

import {
  Home,
  Users,
  FileText,
  ShoppingCart,
  Sheet,
  Fuel,
  Wallet,
  FileBarChart,
  LogOut,
  ChevronRight,
  ChevronDown,
  Layers3,
  CalendarCheck,
} from "lucide-react";


const SIDEBAR_WIDTH_EXPANDED = 200;
const SIDEBAR_WIDTH_COLLAPSED = 64;


const defaultOpenMenus = {
  masters: false,
  tariff: false,
  invoice: false,
  purchase: false,
  fuel: false,
  accountant: false,
};


const Sidebar = ({ onCollapsedChange }) => {
  const location = useLocation();


  /* =======================================================
     MENU STATE

     NOTE: menu open/close is UI state, not navigation, so it
     is stored via history.replaceState (not pushState) to
     avoid interfering with the browser Back button. See
     toggleMenu below.
     ======================================================= */

  const [openMenus, setOpenMenus] = useState(() => {
    const histState = window.history.state?.sidebarMenus;

    if (histState) {
      return histState;
    }

    return defaultOpenMenus;
  });


  const [mobileOpen, setMobileOpen] = useState(false);

  const [isCollapsed, setIsCollapsed] = useState(false);


  /* =======================================================
     PERMISSION STATE

     `menuKeys` holds the exact `menu_key` strings the
     backend already computed for this user (see
     `/api/roles/my-permissions` -> `menu_keys`, derived
     from menu_permissions.can_view = true). No alias
     matching or name normalization is needed — the backend
     is the single source of truth for which menu keys
     exist, so the sidebar just checks membership in this
     array.
     ======================================================= */

  const [menuKeys, setMenuKeys] = useState([]);

  const [permissionsLoading, setPermissionsLoading] =
    useState(true);


  /* =======================================================
     LOAD CURRENT USER PERMISSIONS
     ======================================================= */

  useEffect(() => {
    let mounted = true;


    const loadPermissions = async () => {
      try {
        setPermissionsLoading(true);


        const response = await axiosInstance.get(
          "/api/roles/my-permissions"
        );


        if (!mounted) {
          return;
        }


        setMenuKeys(
          response.data?.menu_keys || []
        );
      } catch (error) {
        console.error(
          "Load sidebar permissions error:",
          error
        );


        if (mounted) {
          setMenuKeys([]);
        }
      } finally {
        if (mounted) {
          setPermissionsLoading(false);
        }
      }
    };


    loadPermissions();


    return () => {
      mounted = false;
    };
  }, []);


  /* =======================================================
     COLLAPSED STATE CALLBACK
     ======================================================= */

  useEffect(() => {
    onCollapsedChange?.(isCollapsed);
  }, [isCollapsed, onCollapsedChange]);


  /* =======================================================
     SAVE MENU STATE TO HISTORY

     Uses replaceState so that merely opening/closing a
     sidebar submenu does not create a new history entry.
     ======================================================= */

  useEffect(() => {
    window.history.replaceState(
      {
        ...window.history.state,
        sidebarMenus: openMenus,
      },
      "",
      window.location.pathname +
        window.location.search
    );
  }, [openMenus, location.pathname]);


  /* =======================================================
     MENU TOGGLE

     IMPORTANT: this uses replaceState, not pushState.
     Menu open/close is transient UI state, not a navigation
     event, so it should never be a distinct entry in
     browser history. Using pushState here previously broke
     the Back button (it would "undo" menu toggles instead
     of navigating to the prior page).
     ======================================================= */

  const toggleMenu = useCallback((menu) => {
    setOpenMenus((prev) => {
      const newMenus = {
        ...prev,
        [menu]: !prev[menu],
      };


      window.history.replaceState(
        {
          ...window.history.state,
          sidebarMenus: newMenus,
        },
        "",
        window.location.pathname +
          window.location.search
      );


      return newMenus;
    });
  }, []);


  /* =======================================================
     BACK BUTTON MENU STATE
     ======================================================= */

  useEffect(() => {
    const onPopState = (event) => {
      const menuState =
        event.state?.sidebarMenus ??
        defaultOpenMenus;


      setOpenMenus(menuState);
    };


    window.addEventListener(
      "popstate",
      onPopState
    );


    return () =>
      window.removeEventListener(
        "popstate",
        onPopState
      );
  }, []);


  /* =======================================================
     ACTIVE ROUTE
     ======================================================= */

  const isActive = (path) => {
    return location.pathname === path;
  };


  /* =======================================================
     PERMISSION CHECK

     `menuKey` must exactly match a `menu_key` from
     MENU_PERMISSION_MASTER on the backend (roleRoutes.js),
     e.g. "customers", "vehicle_group", "fuel_indent",
     "accounts_chart", "accounts_reports".
     ======================================================= */

  const can = useCallback(
    (menuKey) => {
      if (permissionsLoading) {
        return false;
      }


      return menuKeys.includes(menuKey);
    },
    [menuKeys, permissionsLoading]
  );


  /* =======================================================
     CHECK ANY PERMISSION

     Used for parent menus.

     Example:

     Masters should appear if the user has at least
     one Masters permission.
     ======================================================= */

  const canAny = useCallback(
    (...permissionKeys) => {
      return permissionKeys.some((key) =>
        can(key)
      );
    },
    [can]
  );


  /* =======================================================
     MENU LINK CLICK

     Closes the mobile drawer when a link is tapped. Takes no
     arguments (previously called with an unused path
     argument at every call site).
     ======================================================= */

  const handleMenuLinkClick = () => {
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };


  /* =======================================================
     SIDEBAR STYLES
     ======================================================= */

  const baseItem =
    "flex items-center gap-2 rounded px-3 py-2 text-sm font-small transition-colors focus:outline-none";

  const activeClass =
    "bg-[#2a3245] border-l-4 border-blue-500 text-white font-semibold";

  const hoverClass =
    "hover:bg-[#2a3245]";

  const submenuPad =
    "pl-10 text-gray-300";

  const iconPad =
    "min-w-[22px] inline-block text-center flex-shrink-0";


  /* =======================================================
     SIDEBAR CONTENT
     ======================================================= */

  const sidebarContent = (
    collapsed = isCollapsed
  ) => (
    <>
      {/* =================================================
          LOGO / COLLAPSE BUTTON
          ================================================= */}

      <div
        onClick={() =>
          setIsCollapsed(!collapsed)
        }
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (
            e.key === "Enter" ||
            e.key === " "
          ) {
            e.preventDefault();

            setIsCollapsed(!collapsed);
          }
        }}
        aria-label={
          collapsed
            ? "Expand sidebar"
            : "Collapse sidebar"
        }
        title={
          collapsed
            ? "Expand sidebar"
            : "Collapse sidebar"
        }
        className={`relative h-14 flex items-center border-b border-[#2a3245] cursor-pointer select-none hover:bg-[#252c3b] transition-colors ${
          collapsed
            ? "justify-center px-0"
            : "px-3"
        }`}
      >
        <div
          className={`flex items-center ${
            collapsed
              ? "justify-center"
              : "gap-2 pr-10"
          }`}
        >
          <img
            src="/logo.png"
            alt="Logo"
            className="h-10 w-10 p-[2px] rounded flex-shrink-0"
          />


          {!collapsed && (
            <div className="flex flex-col leading-tight whitespace-nowrap">
              <span className="text-white text-[0.75rem] font-normal">
                Lotus
              </span>

              <span className="text-white text-[1rem] whitespace-nowrap">
                <span className="font-semibold">
                  WheelsERP
                </span>{" "}

                <span className="italic font-small text-blue-300">
                  Prime
                </span>
              </span>
            </div>
          )}
        </div>
      </div>


      {/* =================================================
          SIDEBAR MENU
          ================================================= */}

      <div className="flex-1 overflow-y-auto py-2 sidebar-scroll">

        {/* =================================================
            HOME
            Home remains available to every user.
            ================================================= */}

        <Link
          to="/"
          onClick={handleMenuLinkClick}
          className={`${baseItem} ${hoverClass} ${
            isActive("/")
              ? activeClass
              : ""
          } ${
            collapsed
              ? "justify-center px-0"
              : ""
          }`}
          title={
            collapsed
              ? "Home"
              : undefined
          }
        >
          <span className={iconPad}>
            <Home
              size={18}
              className={`${
                isActive("/")
                  ? "opacity-100"
                  : "opacity-40"
              }`}
            />
          </span>

          {!collapsed && "Home"}
        </Link>


        {/* =================================================
            MASTERS
            ================================================= */}

        {canAny(
          "customers",
          "vehicles",
          "vendors",
          "employees",
          "vehicle_group",
          "vehicle_type",
          "hotels"
        ) && (
          <>
            <button
              onClick={() => {
                if (collapsed) {
                  setIsCollapsed(false);
                }

                toggleMenu("masters");
              }}
              className={`w-full ${baseItem} cursor-pointer ${hoverClass} ${
                collapsed
                  ? "justify-center px-0"
                  : "justify-between"
              }`}
            >
              <span className="flex items-center gap-2">

                <span className={iconPad}>
                  <Users
                    size={18}
                    className="opacity-40"
                  />
                </span>

                {!collapsed && "Masters"}
              </span>


              {!collapsed && (
                openMenus.masters ? (
                  <ChevronDown size={16} />
                ) : (
                  <ChevronRight size={16} />
                )
              )}
            </button>


            {!collapsed &&
              openMenus.masters && (
                <div className="mt-1 space-y-0.5">

                  {/* CUSTOMERS */}
                  {can("customers") && (
                    <Link
                      to="/customers"
                      onClick={handleMenuLinkClick}
                      className={`${baseItem} ${submenuPad} ${hoverClass} ${
                        isActive("/customers")
                          ? activeClass
                          : ""
                      }`}
                    >
                      Customers
                    </Link>
                  )}


                  {/* VEHICLES */}
                  {can("vehicles") && (
                    <Link
                      to="/vehicles"
                      onClick={handleMenuLinkClick}
                      className={`${baseItem} ${submenuPad} ${hoverClass} ${
                        isActive("/vehicles")
                          ? activeClass
                          : ""
                      }`}
                    >
                      Vehicles
                    </Link>
                  )}


                  {/* VENDORS */}
                  {can("vendors") && (
                    <Link
                      to="/vendors"
                      onClick={handleMenuLinkClick}
                      className={`${baseItem} ${submenuPad} ${hoverClass} ${
                        isActive("/vendors")
                          ? activeClass
                          : ""
                      }`}
                    >
                      Vendors
                    </Link>
                  )}


                  {/* EMPLOYEES */}
                  {can("employees") && (
                    <Link
                      to="/employees"
                      onClick={handleMenuLinkClick}
                      className={`${baseItem} ${submenuPad} ${hoverClass} ${
                        isActive("/employees")
                          ? activeClass
                          : ""
                      }`}
                    >
                      Employees
                    </Link>
                  )}


                  {/* VEHICLE GROUP */}
                  {can("vehicle_group") && (
                    <Link
                      to="/vehicle-group"
                      onClick={handleMenuLinkClick}
                      className={`${baseItem} ${submenuPad} ${hoverClass} ${
                        isActive(
                          "/vehicle-group"
                        )
                          ? activeClass
                          : ""
                      }`}
                    >
                      Vehicle Group
                    </Link>
                  )}


                  {/* VEHICLE TYPE */}
                  {can("vehicle_type") && (
                    <Link
                      to="/vehicle-type"
                      onClick={handleMenuLinkClick}
                      className={`${baseItem} ${submenuPad} ${hoverClass} ${
                        isActive(
                          "/vehicle-type"
                        )
                          ? activeClass
                          : ""
                      }`}
                    >
                      Vehicle Type
                    </Link>
                  )}


                  {/* HOTELS */}
                  {can("hotels") && (
                    <Link
                      to="/hotels"
                      onClick={handleMenuLinkClick}
                      className={`${baseItem} ${submenuPad} ${hoverClass} ${
                        isActive("/hotels")
                          ? activeClass
                          : ""
                      }`}
                    >
                      Hotels
                    </Link>
                  )}

                </div>
              )}
          </>
        )}


        {/* =================================================
            BOOKING
            ================================================= */}

        {can("booking") && (
          <Link
            to="/bookings"
            onClick={handleMenuLinkClick}
            className={`${baseItem} ${hoverClass} ${
              isActive("/bookings")
                ? activeClass
                : ""
            } ${
              collapsed
                ? "justify-center px-0"
                : ""
            }`}
            title={
              collapsed
                ? "Booking"
                : undefined
            }
          >
            <span className={iconPad}>
              <CalendarCheck
                size={18}
                className={`${
                  isActive("/bookings")
                    ? "opacity-100"
                    : "opacity-40"
                }`}
              />
            </span>

            {!collapsed && "Booking"}
          </Link>
        )}


        {/* =================================================
            TRIPSHEETS
            ================================================= */}

        {can("tripsheets") && (
          <Link
            to="/tripsheets"
            onClick={handleMenuLinkClick}
            className={`${baseItem} ${hoverClass} ${
              isActive("/tripsheets")
                ? activeClass
                : ""
            } ${
              collapsed
                ? "justify-center px-0"
                : ""
            }`}
            title={
              collapsed
                ? "Tripsheets"
                : undefined
            }
          >
            <span className={iconPad}>
              <Sheet
                size={18}
                className={`${
                  isActive("/tripsheets")
                    ? "opacity-100"
                    : "opacity-40"
                }`}
              />
            </span>

            {!collapsed && "Tripsheets"}
          </Link>
        )}


        {/* =================================================
            PURCHASE
            ================================================= */}

        {can("purchase") && (
          <>
            <button
              onClick={() => {
                if (collapsed) {
                  setIsCollapsed(false);
                }

                toggleMenu("purchase");
              }}
              className={`w-full ${baseItem} cursor-pointer ${hoverClass} ${
                collapsed
                  ? "justify-center px-0"
                  : "justify-between"
              }`}
            >
              <span className="flex items-center gap-2">

                <span className={iconPad}>
                  <ShoppingCart
                    size={18}
                    className="opacity-40"
                  />
                </span>

                {!collapsed && "Purchase"}
              </span>


              {!collapsed && (
                openMenus.purchase ? (
                  <ChevronDown size={16} />
                ) : (
                  <ChevronRight size={16} />
                )
              )}
            </button>


            {!collapsed &&
              openMenus.purchase && (
                <div className="mt-1 space-y-0.5">

                  <Link
                    to="/purchases"
                    onClick={handleMenuLinkClick}
                    className={`${baseItem} ${submenuPad} ${hoverClass} ${
                      isActive("/purchases")
                        ? activeClass
                        : ""
                    }`}
                  >
                    Purchase
                  </Link>

                </div>
              )}
          </>
        )}


        {/* =================================================
            INVOICE
            ================================================= */}

        {canAny(
          "invoice",
          "proforma"
        ) && (
          <>
            <button
              onClick={() => {
                if (collapsed) {
                  setIsCollapsed(false);
                }

                toggleMenu("invoice");
              }}
              className={`w-full ${baseItem} cursor-pointer ${hoverClass} ${
                collapsed
                  ? "justify-center px-0"
                  : "justify-between"
              }`}
            >
              <span className="flex items-center gap-2">

                <span className={iconPad}>
                  <FileText
                    size={18}
                    className="opacity-40"
                  />
                </span>

                {!collapsed && "Invoice"}
              </span>


              {!collapsed && (
                openMenus.invoice ? (
                  <ChevronDown size={16} />
                ) : (
                  <ChevronRight size={16} />
                )
              )}
            </button>


            {!collapsed &&
              openMenus.invoice && (
                <div className="mt-1 space-y-0.5">

                  {/* INVOICE */}
                  {can("invoice") && (
                    <Link
                      to="/invoices"
                      onClick={handleMenuLinkClick}
                      className={`${baseItem} ${submenuPad} ${hoverClass} ${
                        isActive("/invoice")
                          ? activeClass
                          : ""
                      }`}
                    >
                      Invoice
                    </Link>
                  )}


                  {/* PERFORMA */}
                  {can("proforma") && (
                    <Link
                      to="/performa"
                      onClick={handleMenuLinkClick}
                      className={`${baseItem} ${submenuPad} ${hoverClass} ${
                        isActive("/performa")
                          ? activeClass
                          : ""
                      }`}
                    >
                      Performa
                    </Link>
                  )}

                </div>
              )}
          </>
        )}


        {/* =================================================
            FUEL INDENT
            ================================================= */}

        {canAny(
          "bunks",
          "fuel_indent"
        ) && (
          <>
            <button
              onClick={() => {
                if (collapsed) {
                  setIsCollapsed(false);
                }

                toggleMenu("fuel");
              }}
              className={`w-full ${baseItem} cursor-pointer ${hoverClass} ${
                collapsed
                  ? "justify-center px-0"
                  : "justify-between"
              }`}
            >
              <span className="flex items-center gap-2">

                <span className={iconPad}>
                  <Fuel
                    size={18}
                    className="opacity-40"
                  />
                </span>

                {!collapsed &&
                  "Fuel Indent"}
              </span>


              {!collapsed && (
                openMenus.fuel ? (
                  <ChevronDown size={16} />
                ) : (
                  <ChevronRight size={16} />
                )
              )}
            </button>


            {!collapsed &&
              openMenus.fuel && (
                <div className="mt-1 space-y-0.5">

                  {/* BUNKS */}
                  {can("bunks") && (
                    <Link
                      to="/bunks"
                      onClick={handleMenuLinkClick}
                      className={`${baseItem} ${submenuPad} ${hoverClass} ${
                        isActive("/bunks")
                          ? activeClass
                          : ""
                      }`}
                    >
                      Bunks
                    </Link>
                  )}


                  {/* INDENT */}
                  {can("fuel_indent") && (
                    <Link
                      to="/indent"
                      onClick={handleMenuLinkClick}
                      className={`${baseItem} ${submenuPad} ${hoverClass} ${
                        isActive("/indent")
                          ? activeClass
                          : ""
                      }`}
                    >
                      Indent
                    </Link>
                  )}

                </div>
              )}
          </>
        )}


        {/* =================================================
            ACCOUNTANT
            ================================================= */}

        {canAny(
          "accounts_chart",
          "vouchers",
          "accounts_reports"
        ) && (
          <>
            <button
              onClick={() => {
                if (collapsed) {
                  setIsCollapsed(false);
                }

                toggleMenu("accountant");
              }}
              className={`w-full ${baseItem} cursor-pointer ${hoverClass} ${
                collapsed
                  ? "justify-center px-0"
                  : "justify-between"
              }`}
            >
              <span className="flex items-center gap-2">

                <span className={iconPad}>
                  <Wallet
                    size={18}
                    className="opacity-40"
                  />
                </span>

                {!collapsed &&
                  "Accountant"}
              </span>


              {!collapsed && (
                openMenus.accountant ? (
                  <ChevronDown size={16} />
                ) : (
                  <ChevronRight size={16} />
                )
              )}
            </button>


            {!collapsed &&
              openMenus.accountant && (
                <div className="mt-1 space-y-0.5">

                  {/* ACCOUNTS CHART */}
                  {can("accounts_chart") && (
                    <Link
                      to="/accounts-chart"
                      onClick={handleMenuLinkClick}
                      className={`${baseItem} ${submenuPad} ${hoverClass} ${
                        isActive(
                          "/accounts-chart"
                        )
                          ? activeClass
                          : ""
                      }`}
                    >
                      Accounts Chart
                    </Link>
                  )}


                  {/* VOUCHERS */}
                  {can("vouchers") && (
                    <Link
                      to="/vouchers"
                      onClick={handleMenuLinkClick}
                      className={`${baseItem} ${submenuPad} ${hoverClass} ${
                        isActive("/vouchers")
                          ? activeClass
                          : ""
                      }`}
                    >
                      Vouchers
                    </Link>
                  )}


                  {/* ACCOUNTS REPORTS */}
                  {can("accounts_reports") && (
                    <Link
                      to="/accountant/accounts-reports"
                      onClick={handleMenuLinkClick}
                      className={`${baseItem} ${submenuPad} ${hoverClass} ${
                        isActive(
                          "/accountant/accounts-reports"
                        )
                          ? activeClass
                          : ""
                      }`}
                    >
                      Accounts Reports
                    </Link>
                  )}

                </div>
              )}
          </>
        )}


        {/* =================================================
            REPORTS
            ================================================= */}

        {can("reports") && (
          <Link
            to="/reports"
            onClick={handleMenuLinkClick}
            className={`${baseItem} ${hoverClass} ${
              isActive("/reports")
                ? activeClass
                : ""
            } ${
              collapsed
                ? "justify-center px-0"
                : ""
            }`}
            title={
              collapsed
                ? "Reports"
                : undefined
            }
          >
            <span className={iconPad}>
              <FileBarChart
                size={18}
                className={`${
                  isActive("/reports")
                    ? "opacity-100"
                    : "opacity-40"
                }`}
              />
            </span>

            {!collapsed && "Reports"}
          </Link>
        )}

      </div>


      {/* =================================================
          LOGOUT
          ================================================= */}

      <div className="p-2 border-t border-[#2a3245]">

        <button
          onClick={async () => {
            try {
              await axiosInstance.post(
                "/api/auth/logout"
              );
            } catch (error) {
              console.error(
                "Logout API error:",
                error
              );
            } finally {
              localStorage.clear();
              window.location.href =
                "/login";
            }
          }}
          className={`flex items-center gap-2 px-3 py-2 rounded hover:bg-[#2a3245] w-full text-left text-white ${
            collapsed
              ? "justify-center px-0"
              : ""
          }`}
          title={
            collapsed
              ? "Logout"
              : undefined
          }
        >
          <span className={iconPad}>
            <LogOut
              size={18}
              className="opacity-40"
            />
          </span>

          {!collapsed && "Logout"}
        </button>

      </div>
    </>
  );


  /* =======================================================
     MAIN RENDER
     ======================================================= */

  return (
    <>
      {/* =================================================
          MOBILE TOGGLE
          ================================================= */}

      <button
        onClick={() =>
          setMobileOpen(true)
        }
        aria-label="Open sidebar"
        className="fixed top-4 left-4 z-50 p-2 rounded bg-gray-700 text-white md:hidden"
      >
        <Layers3
          size={24}
          className="opacity-40"
        />
      </button>


      {/* =================================================
          MOBILE OVERLAY
          ================================================= */}

      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex">

          <div
            className="fixed inset-0 bg-black opacity-50"
            onClick={() =>
              setMobileOpen(false)
            }
          />


          <div className="relative flex flex-col bg-[#1F2533] text-white w-[200px] max-w-[200px] min-w-[200px] flex-shrink-0">

            <button
              onClick={() =>
                setMobileOpen(false)
              }
              aria-label="Close sidebar"
              className="p-2 m-2 rounded bg-gray-700 text-white self-start"
            >
              ×
            </button>


            {sidebarContent(false)}

          </div>

        </div>
      )}


      {/* =================================================
          DESKTOP SIDEBAR
          ================================================= */}

      <div
        className="hidden md:flex fixed top-0 left-0 h-screen bg-[#1F2533] text-white z-40 flex flex-col select-none overflow-hidden"
        style={{
          width: isCollapsed
            ? SIDEBAR_WIDTH_COLLAPSED
            : SIDEBAR_WIDTH_EXPANDED,

          minWidth: isCollapsed
            ? SIDEBAR_WIDTH_COLLAPSED
            : SIDEBAR_WIDTH_EXPANDED,

          transition:
            "width 200ms ease-in-out",
        }}
      >
        {sidebarContent(
          isCollapsed
        )}
      </div>
    </>
  );
};


export default Sidebar;
